import moment from 'moment'
import { useState, useMemo, useCallback, useEffect } from 'react'
import { View, Views } from 'react-big-calendar'
import TaskCalendar from '../../ws/TaskCalendar'
import CalendarView from './CalendarView'
import { Event } from '../../model/domain/event'
import { ShareCenterOption } from '../../model/domain/share-option'
import { User } from '../../model/domain/user'

interface DateRange {
    start: Date
    end: Date
}

interface CalendarContainerState {
    user?: User
    events: Event[]
    shareOptions?: ShareCenterOption[]
    pivotDate: Date
    view: View
    showSaveModal: boolean
    loadingUser: boolean
    loadingShareOptions: boolean
    loadingEvents: boolean
    loadingCreateEvent: boolean
    loadingDeleteEvent: boolean
    error: string | null
    saveSuccess: boolean
    showDeleteModal: boolean
    eventIdToDelete: number | null
    deleteSuccess: boolean
    loadingDetail: boolean
    event: Event | null
}

export default function CalendarContainer() {
    const [state, setState] = useState<CalendarContainerState>({
        events: [],
        pivotDate: new Date(),
        view: Views.MONTH,
        showSaveModal: false,
        loadingUser: false,
        loadingShareOptions: false,
        loadingEvents: false,
        loadingCreateEvent: false,
        loadingDeleteEvent: false,
        error: null,
        saveSuccess: false,
        showDeleteModal: false,
        eventIdToDelete: null,
        deleteSuccess: false,
        loadingDetail: false,
        event: null,
    })

    const {
        user,
        events,
        shareOptions,
        pivotDate,
        view,
        showSaveModal,
        loadingUser,
        loadingEvents,
        loadingCreateEvent,
        loadingDeleteEvent,
        loadingShareOptions,
        error,
        saveSuccess,
        showDeleteModal,
        eventIdToDelete,
        deleteSuccess,
        loadingDetail,
        event,
    } = state

    const updateState = (changes: Partial<CalendarContainerState>) => {
        setState((state) => ({
            ...state,
            ...changes,
        }))
    }

    const task = useMemo(() => new TaskCalendar(), [])

    const fetchUser = useCallback(async () => {
        try {
            updateState({ loadingUser: true })
            const response = await task.getUser()
            updateState({ user: response })
        } catch (err) {
            if (err instanceof Error) {
                updateState({ error: err.message })
            }
        } finally {
            updateState({ loadingUser: false })
        }
    }, [task])

    useEffect(() => {
        fetchUser()
    }, [fetchUser])

    const fetchShareOptions = useCallback(async () => {
        try {
            updateState({ loadingShareOptions: true })
            const response = await task.getShareOptions()
            updateState({ shareOptions: response })
        } catch (err) {
            if (err instanceof Error) {
                updateState({ error: err.message })
            }
        } finally {
            updateState({ loadingShareOptions: false })
        }
    }, [task])

    useEffect(() => {
        fetchShareOptions()
    }, [fetchShareOptions])

    const fetchEvents = useCallback(
        async (range: DateRange) => {
            console.log(
                `${range.start.toLocaleDateString()} - ${range.end.toLocaleDateString()}`
            )
            try {
                updateState({
                    loadingEvents: true,
                    error: null,
                })
                const events = await task.getEvents(range.start, range.end)
                updateState({ events })
            } catch (err) {
                if (err instanceof Error) {
                    updateState({ error: err.message })
                }
            } finally {
                updateState({ loadingEvents: false })
            }
        },
        [task]
    )

    useEffect(() => {
        const dateRange = getViewRange(pivotDate, view)
        fetchEvents(dateRange)
    }, [pivotDate, view, fetchEvents, saveSuccess, deleteSuccess])

    const getViewRange = (pivotDate: Date, view: View) => {
        const momentDate = moment(pivotDate)
        const unitOfTime = view === Views.WEEK ? 'isoWeek' : 'month'

        return {
            start: momentDate.startOf(unitOfTime).toDate(),
            end: momentDate.endOf(unitOfTime).toDate(),
        }
    }

    const handleNavigate = (newDate: Date) => {
        updateState({ pivotDate: newDate })
    }

    const handleViewChange = (view: View) => {
        updateState({ view })
    }

    const handleOpenSaveModal = () => {
        updateState({ showSaveModal: true, event: null })
    }

    const getMappedEvents = () => {
        return events.map((event) => {
            const endDate = new Date(
                event.allDay ? event.startDate : event.endDate!
            )
            if (event.allDay) {
                endDate.setHours(23, 59, 59)
            }

            return {
                start: new Date(event.startDate),
                end: endDate,
                ...event,
            }
        })
    }

    const handleCancelSaveModal = () => {
        updateState({ showSaveModal: false })
    }

    const handleConfirmSaveModal = (event: Event) => {
        updateState({ showSaveModal: false })
        fetchSaveEvent(event)
    }

    const fetchSaveEvent = async (event) => {
        try {
            updateState({
                saveSuccess: false,
                deleteSuccess: false,
                loadingCreateEvent: true,
                error: null,
            })
            await task.saveEvent(event)
            updateState({ saveSuccess: true })
        } catch (err) {
            if (err instanceof Error) {
                updateState({ error: err.message })
            }
        } finally {
            updateState({ loadingCreateEvent: false })
        }
    }

    const handleOpenEditModal = async (eventId: number) => {
        try {
            updateState({
                showSaveModal: true,
                loadingDetail: true,
            })
            const detailedEvent = await task.getEventDetail(eventId)
            updateState({
                event: detailedEvent,
            })
        } catch (err) {
            if (err instanceof Error) {
                updateState({
                    error: err.message,
                    showSaveModal: false,
                    event: null,
                })
            }
        } finally {
            updateState({ loadingDetail: false })
        }
    }

    const handleOpenDeleteModal = (eventId: number) => {
        updateState({
            eventIdToDelete: eventId,
            showDeleteModal: true,
        })
    }

    const handleCancelDeleteModal = () => {
        updateState({
            eventIdToDelete: null,
            showDeleteModal: false,
        })
    }

    const handleConfirmDeleteModal = () => {
        updateState({ showDeleteModal: false })
        fetchDeleteEvent()
    }

    const fetchDeleteEvent = async () => {
        if (eventIdToDelete) {
            try {
                updateState({
                    saveSuccess: false,
                    deleteSuccess: false,
                    loadingDeleteEvent: true,
                    error: null,
                })
                await task.deleteEvent(eventIdToDelete)
                updateState({ deleteSuccess: true })
            } catch (err) {
                if (err instanceof Error) {
                    updateState({ error: err.message })
                }
            } finally {
                updateState({ loadingDeleteEvent: false })
            }
        }
    }

    const loading = useMemo(() => {
        return (
            loadingUser ||
            loadingEvents ||
            loadingCreateEvent ||
            loadingDeleteEvent
        )
    }, [loadingUser, loadingEvents, loadingCreateEvent, loadingDeleteEvent])

    return (
        <CalendarView
            user={user}
            mappedEvents={getMappedEvents()}
            shareOptions={shareOptions}
            loadingShareOptions={loadingShareOptions}
            view={view}
            showSaveModal={showSaveModal}
            showDeleteModal={showDeleteModal}
            loading={loading}
            error={error}
            saveSuccess={saveSuccess}
            deleteSuccess={deleteSuccess}
            loadingDetail={loadingDetail}
            event={event}
            onView={handleViewChange}
            onNavigate={handleNavigate}
            onOpenCreateEvent={handleOpenSaveModal}
            onCancelCreateEvent={handleCancelSaveModal}
            onConfirmCreateEvent={handleConfirmSaveModal}
            onOpenEditEvent={handleOpenEditModal}
            onOpenDeleteEvent={handleOpenDeleteModal}
            onCancelDeleteEvent={handleCancelDeleteModal}
            onConfirmDeleteEvent={handleConfirmDeleteModal}
        />
    )
}
